import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Button,
  Alert
} from '@mui/material';
import { HierarchicalTreeSelector } from '../components/common/tree';
import permissionService from '../services/permissionService';

/**
 * Test page for HierarchicalTreeSelector component
 *
 * Tests both modes:
 * 1. View-only mode (Permission Management style)
 * 2. Checkbox selection mode (Role Management style)
 */
const TreeComponentTest = () => {
  const [tab, setTab] = useState(0);
  const [category, setCategory] = useState('system');
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  // Fetch permission tree
  const fetchTree = async (cat) => {
    try {
      setLoading(true);
      setError(null);
      const response = await permissionService.getPermissionTree(cat);
      if (response.success) {
        setTreeData(response.data.tree || []);
      } else {
        setError(response.error || 'Failed to load tree');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch permission tree');
      console.error('Tree fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Count total nodes in tree
  const countNodes = (nodes) => {
    if (!nodes) return 0;
    let count = nodes.length;
    nodes.forEach(node => {
      if (node.children) {
        count += countNodes(node.children);
      }
    });
    return count;
  };

  // Load tree on mount and category change
  useEffect(() => {
    fetchTree(category);
  }, [category]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    const newCategory = newValue === 0 ? 'system' : 'project';
    setCategory(newCategory);
  };

  // Handle selection change (for checkbox mode)
  const handleSelectionChange = (selected) => {
    setSelectedPermissions(selected);
    console.log('Selected permissions:', selected);
  };

  // Handle node click (for view-only mode)
  const handleNodeClick = (node) => {
    console.log('Node clicked:', node);
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Fixed Header */}
      <Box sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Container maxWidth="xl" sx={{ py: 2 }}>
          <Typography variant="h4" gutterBottom>
            🧪 Tree Component Test Page
          </Typography>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Testing HierarchicalTreeSelector with {countNodes(treeData)} real permissions
          </Typography>

          {/* Category Tabs */}
          <Tabs value={tab} onChange={handleTabChange}>
            <Tab label="System Permissions" />
            <Tab label="Project Permissions" />
          </Tabs>
        </Container>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>

      {/* Test Mode 1: View-Only (Permission Management Style) */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Mode 1: View-Only (Permission Management)
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          No checkboxes, just view structure
        </Typography>

        <Box sx={{ mt: 2 }}>
          <HierarchicalTreeSelector
            treeData={treeData}
            selectionMode="none"
            searchEnabled={true}
            searchPlaceholder="Search permissions..."
            showNodeIcons={true}
            showNodeActions={false}
            showSelectedCount={false}
            expandAllButton={true}
            collapseAllButton={true}
            getNodeId={(node) => node.id}
            onNodeClick={handleNodeClick}
            height={300}
            loading={loading}
            error={error}
          />
        </Box>
      </Paper>

      {/* Test Mode 2: Checkbox Selection (Role Management Style) */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Mode 2: Checkbox Selection (Role Management)
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          With cascade selection and deduplication
        </Typography>
 
        {selectedPermissions.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              Selected Permissions (stored as paths):
            </Typography>
            <Box component="pre" sx={{
              mt: 1,
              fontSize: '0.75rem',
              maxHeight: 100,
              overflow: 'auto',
              backgroundColor: 'rgba(0,0,0,0.05)',
              p: 1,
              borderRadius: 1
            }}>
              {JSON.stringify(selectedPermissions, null, 2)}
            </Box>
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <HierarchicalTreeSelector
            treeData={treeData}
            selectedItems={selectedPermissions}
            onSelectionChange={handleSelectionChange}
            selectionMode="checkbox"
            multiSelect={true}
            cascadeSelection={true}
            deduplicateOnCascade={true}
            searchEnabled={true}
            searchPlaceholder="Search permissions..."
            showNodeIcons={true}
            showNodeActions={false}
            showSelectedCount={true}
            expandAllButton={true}
            collapseAllButton={true}
            getNodeId={(node) => node.path}  // Use PATH for wildcards
            height={300}
            loading={loading}
            error={error}
          />
        </Box>

        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setSelectedPermissions([])}
            disabled={selectedPermissions.length === 0}
          >
            Clear All Selections
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              // Select a few test permissions
              const testSelections = ['dashboard', 'user.view'];
              setSelectedPermissions(testSelections);
            }}
          >
            Test Selection
          </Button>
        </Box>
      </Paper>

      {/* Performance Stats */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          📊 Performance Stats
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Total Nodes
            </Typography>
            <Typography variant="h6">
              {countNodes(treeData)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Root Nodes
            </Typography>
            <Typography variant="h6">
              {treeData.length}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Category
            </Typography>
            <Typography variant="h6">
              {category}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Instructions */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2" fontWeight="bold" gutterBottom>
          Test Checklist:
        </Typography>
        <Typography variant="body2" component="div">
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>✅ Tree loads with 1000+ permissions</li>
            <li>✅ Search filters tree correctly</li>
            <li>✅ Expand/Collapse works</li>
            <li>✅ Mode 1: View-only (no checkboxes)</li>
            <li>✅ Mode 2: Checkbox selection works</li>
            <li>✅ Cascade selection (parent → children)</li>
            <li>✅ Deduplication (removes children when parent checked)</li>
            <li>✅ Indeterminate state (partial selection)</li>
            <li>✅ Performance is smooth</li>
          </ul>
        </Typography>
      </Alert>
        </Container>
      </Box>
    </Box>
  );
};

export default TreeComponentTest;
